import { ofType, combineEpics } from "redux-observable";
import * as Rx from "rxjs";
import * as RxOp from "rxjs/operators";
import * as S from "sanctuary";
import { getPointOfSaleEnabled } from "../../state/client.state";
// API Client
import {
  activateOktaUser,
  checkOktaUser,
  createOktaUser,
  deactivateOktaUser,
  editOktaUser,
  getOktaAdministeredUsers,
  getRoleCombinationsConfig,
  reactivateOktaUser,
} from "@/APIClient";
import { deserializeToSchema } from "@/APIClient/deserializer/deserializer";
import { requestEpic } from "@/APIClient/observable.util";
import { USER } from "@/APIClient/response-schemas/schemas";
import { toTitleCase } from "@/util/common";

// State imports
import {
  ACTIVATE_ADMINISTERED_USER,
  CHECK_ADMINISTERED_USER,
  CREATE_ADMINISTERED_USER,
  DEACTIVATE_ADMINISTERED_USER,
  EDIT_ADMINISTERED_USER,
  REACTIVATE_ADMINISTERED_USER,
  REQUEST_ADMINISTERED_USERS,
  SAVE_ADMINISTERED_USERS,
  activateAdministeredUserFailed,
  activateAdministeredUserSucceeded,
  checkAdministeredUserFailed,
  checkAdministeredUserSucceeded,
  clearModifyUserRequestState,
  createAdministeredUserFailed,
  createAdministeredUserSucceeded,
  deactivateAdministeredUserFailed,
  deactivateAdministeredUserSucceeded,
  editAdministeredUserFailed,
  editAdministeredUserSucceeded,
  getAdministeredUsersFailed,
  getAdministeredUsersSucceeded,
  getPaginatedUsers,
  getPaginationCursor,
  reactivateAdministeredUserSucceeded,
  reactivateAdministeredUserFailed,
  requestAdministeredUsers,
  saveAdministeredUsers,
  savePaginatedUsers,
  setPaginationCursor,
  FETCH_ROLE_COMBINATIONS,
  fetchRoleCombinationsSucceeded,
  fetchRoleCombinationsFailed,
} from "./AdministeredUsers.state";
import {
  adminPageAlertBarAction,
  setClientAdminForm,
} from "@/components/adminPage/AdminPage.state";
import { addAlert, clearAlerts } from "@/components/alert-bar/AlertBar.state";
import {
  getClientIdentifier,
  getCurrentUserAgencies,
} from "@/components/currentUser/CurrentUser.state";
import { getAuthToken } from "@/util/session";
import { setLoadingOverlayIsActive } from "@/state/shared.state";
import {
  SUBMIT_ACTIVATE_ADMINISTERED_USER,
  SUBMIT_DEACTIVATE_ADMINISTERED_USER,
  SUBMIT_REACTIVATE_ADMINISTERED_USER,

  // AlertBarActions
  addInternalUserAlertBarAction,
  editInternalUserAlertBarAction,
  personalInfoAlertBarAction,
  userSettingsPageAlertBarAction,

  // On Success Actions
  closeEditUserForm,
  closePersonalInfoForm,
  openCreateUserForm,
  openToastNotification,
} from "@/components/userSettingsPage/UserSettingsPage.state";

// Constants
import {
  ACTIVE,
  REVENUE_MANAGEMENT,
  SELECT_ALL,
  SELECT_ALL_SLUG,
  NO_ROLE,
  POINT_OF_SALE,
} from "@/sanctuary/typedefs";
import { CLIENT_ADMIN } from "@/constants/roles";
import { CREATE_CLIENT_ADMIN } from "@/constants/formTypes";

const insertAgencyNames = (item, state) =>
  [
    ...(getCurrentUserAgencies(state)?.value ?? []),
    { name: SELECT_ALL, slug: SELECT_ALL_SLUG },
  ].find((agency) => agency?.slug === item?.slug);

const buildAppRoleAccess = (role, pos_role, state) => {
  const isPOSEnabled = getPointOfSaleEnabled(state);
  const access =
    role && role !== NO_ROLE ? [{ platform: REVENUE_MANAGEMENT, role }] : [];
  if (isPOSEnabled && pos_role && pos_role !== NO_ROLE) {
    access.push({ platform: POINT_OF_SALE, role: pos_role });
  }
  return access;
};

const deserializeUser = (user, state) =>
  deserializeToSchema(USER)({
    data: {
      data: {
        user_id: user?.id?.value,
        role: user?.access?.value?.find(
          (item) => item?.platform === REVENUE_MANAGEMENT
        )?.role,
        pos_role: user?.access?.value?.find(
          (item) => item?.platform === POINT_OF_SALE
        )?.role,
        email: user?.profile?.value?.email,
        client: {
          slug: user?.client?.value?.slug,
          brand: user?.client?.value?.slug,
        },
        credentialsProvider: user?.credentialsProvider?.value?.value?.type,
        agencies: user?.agencies?.value?.map((item) => {
          const slug = item?.slug?.value ?? item?.slug;
          return {
            name: insertAgencyNames({ slug }, state)?.name ?? toTitleCase(slug, "-"),
            slug,
          };
        }),
        permissions: [],
        active: user?.status?.value?.value === ACTIVE,
        status: user?.status?.value?.value,
        statusLabel: user?.statusLabel?.value,
        first_name: user?.profile?.value?.first_name,
        last_name: user?.profile?.value?.last_name,
        fullNameFormatted: user?.profile?.value?.fullNameFormatted,
      },
    },
  })?.value;

const getUserFullName = (user) =>
  "profile" in user
    ? user?.profile?.value?.first_name + " " + user?.profile?.value?.last_name
    : user?.firstName?.value + " " + user?.lastName?.value;

const fetchRoleCombinationsEpic = (action$) =>
  action$.pipe(
    ofType(FETCH_ROLE_COMBINATIONS),
    RxOp.flatMap(() =>
      requestEpic({
        requestMethod: getRoleCombinationsConfig,
        requestArgs: {
          token: S.fromMaybe("")(getAuthToken()),
        },
        onSuccess: (res) =>
          Rx.of(
            fetchRoleCombinationsSucceeded({
              roleCombinations: res.value.data.value.role_combinations,
            })
          ),
        onFailure: (error) =>
          // There are no side effects for the
          // fetchRoleCombinationsFailed case; its
          // is essentailly behaving like `Rx.EMPTY`
          Rx.of(
            fetchRoleCombinationsFailed({
              error: error.value,
            })
          ),
      })
    )
  );

const activateAdministeredUserEpic = (action$, state$) =>
  action$.pipe(
    ofType(ACTIVATE_ADMINISTERED_USER, SUBMIT_ACTIVATE_ADMINISTERED_USER),
    RxOp.mergeMap(({ payload: { userId } }) =>
      requestEpic({
        requestMethod: activateOktaUser,
        requestArgs: {
          token: S.fromMaybe("")(getAuthToken()),
          userId,
        },
        onSuccess: (res) =>
          Rx.of(
            activateAdministeredUserSucceeded({
              user: deserializeUser(res.value, state$.value),
              userId,
            }),
            closeEditUserForm(),
            openToastNotification({
              toastContent: getUserFullName(res.value, state$.value) + " Activated.",
            })
          ),
        onFailure: (err) =>
          Rx.of(
            activateAdministeredUserFailed({ error: err.value, userId }),
            editInternalUserAlertBarAction(clearAlerts()),
            editInternalUserAlertBarAction(
              addAlert({
                heading: "User Not Reactivated",
                text: "There was an error when trying to reactivate the user. Please try again.",
                variant: "error",
              })
            )
          ),
      })
    ),
    RxOp.catchError(() =>
      Rx.of(
        clearModifyUserRequestState(),
        editInternalUserAlertBarAction(clearAlerts()),
        editInternalUserAlertBarAction(
          addAlert({
            heading: "Unable to Activate User",
            text: `We are sorry, we were unable to activate this user. Please try again later.`,
            variant: "error",
          })
        )
      )
    )
  );

const reactivateAdministeredUserEpic = (action$, state$) =>
  action$.pipe(
    ofType(REACTIVATE_ADMINISTERED_USER, SUBMIT_REACTIVATE_ADMINISTERED_USER),
    RxOp.mergeMap(({ payload: { userId } }) =>
      Rx.concat(
        Rx.of(setLoadingOverlayIsActive(true)),
        requestEpic({
          requestMethod: reactivateOktaUser,
          requestArgs: {
            token: S.fromMaybe("")(getAuthToken(state$.value)),
            userId,
          },
          onSuccess: () =>
            Rx.of(
              reactivateAdministeredUserSucceeded({
                userId,
              }),
              setLoadingOverlayIsActive(false),
              editInternalUserAlertBarAction(clearAlerts()),
              openToastNotification({
                toastContent: "Activation Link Sent",
              })
            ),
          onFailure: (err) =>
            Rx.of(
              reactivateAdministeredUserFailed({ error: err.value, userId }),
              setLoadingOverlayIsActive(false),
              editInternalUserAlertBarAction(clearAlerts()),
              editInternalUserAlertBarAction(
                addAlert({
                  heading: "User Not Reactivated",
                  text: "There was an error when trying to reactivate the user. Please try again.",
                  variant: "error",
                })
              )
            ),
        })
      )
    ),
    RxOp.catchError(() =>
      Rx.of(
        clearModifyUserRequestState(),
        setLoadingOverlayIsActive(false),
        editInternalUserAlertBarAction(clearAlerts()),
        editInternalUserAlertBarAction(
          addAlert({
            heading: "Unable to Reactivate User",
            text: `We are sorry, we were unable to reactivate this user. Please try again later.`,
            variant: "error",
          })
        )
      )
    )
  );

const deactivateAdministeredUserEpic = (action$, state$) =>
  action$.pipe(
    ofType(DEACTIVATE_ADMINISTERED_USER, SUBMIT_DEACTIVATE_ADMINISTERED_USER),
    RxOp.mergeMap(({ payload: { userId } }) =>
      requestEpic({
        requestMethod: deactivateOktaUser,
        requestArgs: {
          token: S.fromMaybe("")(getAuthToken()),
          userId,
        },
        onSuccess: (res) =>
          Rx.of(
            deactivateAdministeredUserSucceeded({
              user: deserializeUser(res.value, state$.value),
              userId,
            }),
            closeEditUserForm(),
            openToastNotification({
              toastContent:
                getUserFullName(res.value, state$.value) + " Deactivated.",
            })
          ),
        onFailure: (err) =>
          Rx.of(
            deactivateAdministeredUserFailed({ error: err.data, userId }),
            editInternalUserAlertBarAction(clearAlerts()),
            editInternalUserAlertBarAction(
              addAlert({
                heading: "User Not Deactivated",
                text: "There was an error when trying to deactivate the user. Please try again.",
                variant: "error",
              })
            )
          ),
      })
    ),
    RxOp.catchError(() =>
      Rx.of(
        clearModifyUserRequestState(),
        editInternalUserAlertBarAction(clearAlerts()),
        editInternalUserAlertBarAction(
          addAlert({
            heading: "Unable to Deactivate User",
            text: `We are sorry, we were unable to deactivate this user. Please try again later.`,
            variant: "error",
          })
        )
      )
    )
  );

const buildEditOktaUserArgs = (payload, state) => {
  const isPOSEnabled = getPointOfSaleEnabled(state);
  const editedUser = state?.administeredUsers?.users?.value?.find(
    (user) => user?.id?.value === payload?.userId
  );
  const editedUserAgencySlugs =
    editedUser?.agencies?.value?.map((agency) => agency?.slug) ?? [];
  const grantAgencies = payload?.agencies?.filter(
    (agency) => !editedUserAgencySlugs?.includes(agency)
  );
  const revokeAgencies =
    payload?.role === CLIENT_ADMIN || payload?.pos_role === CLIENT_ADMIN
      ? editedUserAgencySlugs
      : editedUserAgencySlugs?.filter(
          (agency) => !payload?.agencies?.includes(agency)
        );
  const profile = {
    first_name: payload?.firstName,
    last_name: payload?.lastName,
  };

  const posGrantAccessArguments =
    isPOSEnabled &&
    payload?.pos_role &&
    payload.pos_role !== NO_ROLE &&
    payload.pos_role !== editedUser?.pos_role?.value?.value
      ? [{ platform: POINT_OF_SALE, role: payload?.pos_role }]
      : [];
  const revmGrantAccessArguments =
    payload?.role &&
    payload.role !== NO_ROLE &&
    payload.role !== editedUser?.role?.value?.value
      ? [{ platform: REVENUE_MANAGEMENT, role: payload?.role }]
      : [];
  const posRevokeAccessArguments =
    isPOSEnabled &&
    editedUser?.pos_role?.value?.value &&
    editedUser.pos_role.value.value !== NO_ROLE &&
    payload?.pos_role !== editedUser.pos_role.value.value
      ? [{ platform: POINT_OF_SALE, role: editedUser.pos_role.value.value }]
      : [];
  const revmRevokeAccessArguments =
    editedUser?.role?.value?.value &&
    editedUser.role.value.value !== NO_ROLE &&
    payload?.role !== editedUser.role.value?.value
      ? [{ platform: REVENUE_MANAGEMENT, role: editedUser.role.value.value }]
      : [];
  const grantAccess = revmGrantAccessArguments.concat(posGrantAccessArguments);
  const revokeAccess = revmRevokeAccessArguments.concat(posRevokeAccessArguments);

  return {
    ...((payload?.firstName !== editedUser?.firstName?.value ||
      payload?.lastName !== editedUser?.lastName?.value) && {
      profile,
    }),
    ...{ grantAccess, revokeAccess },
    ...(Array.isArray(grantAgencies) &&
      grantAgencies?.length && {
        grantAgencies: [...grantAgencies.map((agency) => ({ slug: agency }))],
      }),
    ...(Array.isArray(revokeAgencies) &&
      revokeAgencies?.length && {
        revokeAgencies: [...revokeAgencies.map((agency) => ({ slug: agency }))],
      }),
  };
};

const filterChanges = (payload) =>
  payload && payload.constructor === Object && Object.keys(payload).length;

const editAdministeredUserEpic = (action$, state$) =>
  action$.pipe(
    ofType(EDIT_ADMINISTERED_USER),
    RxOp.filter(({ payload }) =>
      filterChanges(buildEditOktaUserArgs(payload, state$.value))
    ),
    RxOp.mergeMap(({ payload }) =>
      requestEpic({
        requestMethod: editOktaUser,
        requestArgs: {
          token: S.fromMaybe("")(getAuthToken()),
          userId: payload?.userId,
          ...buildEditOktaUserArgs(payload, state$.value),
        },
        onSuccess: (res) =>
          Rx.of(
            editAdministeredUserSucceeded({
              user: deserializeUser(res.value, state$.value),
              userId: payload.userId,
            }),
            userSettingsPageAlertBarAction(clearAlerts()),
            userSettingsPageAlertBarAction(
              addAlert({
                heading: "Edit User Successful",
                text: `You have successfully edited ${payload.firstName} ${payload.lastName}`,
                variant: "success",
              })
            )
          ),
        onFailure: (err) =>
          Rx.of(
            editAdministeredUserFailed({
              error: err.value,
              userId: payload.userId,
            }),
            editInternalUserAlertBarAction(clearAlerts()),
            editInternalUserAlertBarAction(
              addAlert({
                heading: "Unable to Edit User",
                text:
                  err?.value?.response?.status === 422
                    ? `${err.value.response.data.errors
                        .map((error) => error.detail)
                        .join(" ")}`
                    : `We are sorry, we were unable to edit ${payload.firstName} ${payload.lastName} Please try again later.`,
                variant: "error",
              })
            )
          ),
      })
    ),
    RxOp.catchError(() =>
      Rx.of(
        clearModifyUserRequestState(),
        editInternalUserAlertBarAction(clearAlerts()),
        editInternalUserAlertBarAction(
          addAlert({
            heading: "Unable to Edit User",
            text: `We are sorry, we were unable to edit this user. Please try again later.`,
            variant: "error",
          })
        )
      )
    )
  );

const checkAdministeredUserEpic = (action$) =>
  action$.pipe(
    ofType(CHECK_ADMINISTERED_USER),
    RxOp.mergeMap(({ payload }) =>
      requestEpic({
        requestMethod: checkOktaUser,
        requestArgs: {
          email: payload?.email,
          token: S.fromMaybe("")(getAuthToken()),
        },
        onSuccess: (_res) =>
          Rx.of(
            checkAdministeredUserSucceeded(),
            personalInfoAlertBarAction(clearAlerts()),
            personalInfoAlertBarAction(
              addAlert({
                heading: "User Already Exists",
                text: "The user you are trying to add already exists. Please return to the user list to edit that user’s permissions and roles from their settings page.",
                variant: "error",
              })
            )
          ),
        onFailure: (err) =>
          // 410 in this case means that no database (Okta) record was found.
          // 410 is used instead of 404 because 404 is returned when an endpoint does not exist.
          err.value.status === 410
            ? Rx.of(
                checkAdministeredUserSucceeded(),
                closePersonalInfoForm(),
                openCreateUserForm(payload)
              )
            : Rx.of(
                checkAdministeredUserFailed({ error: err.value }),
                personalInfoAlertBarAction(clearAlerts()),
                personalInfoAlertBarAction(
                  addAlert({
                    heading: "Something Went Wrong",
                    text: "Something went wrong, please try again in a few moments.",
                    variant: "error",
                  })
                )
              ),
      })
    ),
    RxOp.catchError((err) =>
      Rx.of(
        checkAdministeredUserFailed({ error: err.value }),
        personalInfoAlertBarAction(clearAlerts()),
        personalInfoAlertBarAction(
          addAlert({
            heading: "Something Went Wrong",
            text: `Something went wrong, please try again in a few moments.`,
            variant: "error",
          })
        )
      )
    )
  );

/**
 * REDUX EPIC FOR USER CREATION FLOWS
 *
 * This epic creates an Okta user via UMS (/api/v1/sso/users) if the current user is an Okta user, *OR* is a CB_Admin and has specified an authentication type of Okta ("sso") in the CREATE_CLIENT_ADMIN form.
 *
 * User Creation Routes and Services Doc on Confluence:
 * https://citybase.atlassian.net/wiki/spaces/PROD/pages/3420815370/User+Creation+API+Routes+for+SSO+and+Legacy+Users
 *
 * @param {*} action$
 * @param {*} state$
 * @returns action$
 */
const createAdministeredUserEpic = (action$, state$) =>
  action$.pipe(
    ofType(CREATE_ADMINISTERED_USER),
    RxOp.mergeMap(
      ({
        payload: {
          role,
          pos_role = NO_ROLE,
          email,
          client = getClientIdentifier(state$.value),
          agencies,
          firstName,
          lastName,
          formType,
        },
      }) =>
        requestEpic({
          requestMethod: createOktaUser,
          requestArgs: {
            token: S.fromMaybe("")(getAuthToken()),
            firstName,
            lastName,
            email,
            appRoleAccess: buildAppRoleAccess(role, pos_role, state$.value),
            agencies: agencies.map((agency) => ({ slug: agency })),
            client,
          },
          onSuccess: (res) =>
            formType === CREATE_CLIENT_ADMIN
              ? Rx.of(
                  createAdministeredUserSucceeded({
                    user: deserializeUser(res.value, state$.value),
                  }),
                  adminPageAlertBarAction(clearAlerts()),
                  adminPageAlertBarAction(
                    addAlert({
                      heading: "Create Client Admin Successful",
                      text: `You have successfully created ${firstName} ${lastName} as a Client Admin`,
                      variant: "success",
                    })
                  ),
                  setClientAdminForm(false)
                )
              : Rx.of(
                  createAdministeredUserSucceeded({
                    user: deserializeUser(res.value, state$.value),
                  }),
                  userSettingsPageAlertBarAction(clearAlerts()),
                  userSettingsPageAlertBarAction(
                    addAlert({
                      heading: "Create User Successful",
                      text: `You have successfully created ${firstName} ${lastName}`,
                      variant: "success",
                    })
                  )
                ),
          onFailure: (err) =>
            Rx.of(
              createAdministeredUserFailed({ error: err.value }),
              addInternalUserAlertBarAction(clearAlerts()),
              addInternalUserAlertBarAction(
                addAlert({
                  heading: "Unable to Create User",
                  text: `We are sorry, we were unable to create ${firstName} ${lastName} Please try again later.`,
                  variant: "error",
                })
              )
            ),
        })
    ),
    RxOp.catchError((err) => createAdministeredUserFailed({ error: err.message }))
  );

const saveAdministeredUsersEpic = (action$, state$) =>
  action$.pipe(
    ofType(SAVE_ADMINISTERED_USERS),
    RxOp.mergeMap(() =>
      Rx.of(
        getAdministeredUsersSucceeded({
          users: getPaginatedUsers(state$.value),
        })
      )
    )
  );

const requestAdministeredUsersEpic = (action$, state$) =>
  action$.pipe(
    ofType(REQUEST_ADMINISTERED_USERS),
    RxOp.mergeMap(() =>
      requestEpic({
        requestMethod: getOktaAdministeredUsers,
        requestArgs: {
          token: S.fromMaybe("")(getAuthToken()),
          client: getClientIdentifier(state$.value),
          cursor: getPaginationCursor(state$.value),
        },
        onSuccess: (res) =>
          Rx.of(
            savePaginatedUsers(
              res.value.users.map((user) => deserializeUser(user, state$.value))
            ),
            setPaginationCursor(S.fromMaybe("")(res.value.meta.value.pageCursor)),
            res.value.meta.value.pageCursor.value
              ? requestAdministeredUsers()
              : saveAdministeredUsers()
          ),
        onFailure: (err) => Rx.of(getAdministeredUsersFailed({ error: err.value })),
      })
    ),
    RxOp.catchError((err) =>
      Rx.of(getAdministeredUsersFailed({ error: err.message }))
    )
  );

const epic = combineEpics(
  activateAdministeredUserEpic,
  checkAdministeredUserEpic,
  createAdministeredUserEpic,
  deactivateAdministeredUserEpic,
  editAdministeredUserEpic,
  requestAdministeredUsersEpic,
  saveAdministeredUsersEpic,
  fetchRoleCombinationsEpic,
  reactivateAdministeredUserEpic
);
export default epic;
